import { useEffect, useState } from 'react';

/**
 * The one breakpoint in the app (#53, wireframes S1a/S1b/S1c).
 *
 * Teams forbids horizontal scrolling of the tab canvas, and the Teams mobile
 * client is a real target — below this width a data table cannot show its
 * columns without scrolling sideways, so tables become labelled cards instead.
 *
 * Two mechanisms use it, deliberately:
 *
 * - **This hook**, for decisions about *which markup renders* — a table or a
 *   list of cards. A card is not a restyled row: it needs its own labels, so
 *   the choice has to happen in React, and it is the part that can actually be
 *   asserted in a test (jsdom has no layout engine and never evaluates a media
 *   query, so a CSS-only toggle is untestable by construction).
 * - **Griffel `@media` rules** in each page's `makeStyles`, for everything that
 *   is pure layout: multi-column forms collapsing to one column, and the 40px
 *   input / 44px button touch targets. Those need a real CSS engine to mean
 *   anything, and re-implementing them in JS would only make them worse.
 *
 * Griffel needs a literal string for an at-rule key, so page styles spell the
 * query out as `'@media (max-width: 700px)'`. `useIsNarrowViewport.test.ts`
 * pins this constant at 700 so the two cannot drift apart unnoticed.
 */
export const MOBILE_BREAKPOINT_PX = 700;

export const NARROW_VIEWPORT_QUERY = `(max-width: ${String(MOBILE_BREAKPOINT_PX)}px)`;

/**
 * jsdom implements no `matchMedia` at all (it is simply absent, not a stub),
 * and this runs during render — so a missing implementation has to mean "not
 * narrow" rather than a crash. Tests that care mock it explicitly; see
 * `src/test/viewport.ts`.
 */
function matchesNarrowViewport(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(NARROW_VIEWPORT_QUERY).matches;
}

/**
 * True while the tab canvas is at or below {@link MOBILE_BREAKPOINT_PX}.
 *
 * Subscribes to the media query itself rather than to `resize`: the browser
 * only notifies on an actual crossing of the breakpoint, so dragging a window
 * across a hundred pixels re-renders once, not a hundred times.
 */
export function useIsNarrowViewport(): boolean {
  const [isNarrow, setIsNarrow] = useState(matchesNarrowViewport);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(NARROW_VIEWPORT_QUERY);
    // Re-read on mount: the viewport can have changed between the first render
    // and this effect, and a test can install its mock in between.
    setIsNarrow(query.matches);
    function handleChange(event: MediaQueryListEvent) {
      setIsNarrow(event.matches);
    }
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return isNarrow;
}
